import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Gauge, Ruler, Thermometer } from "lucide-react";
import { PageHeader } from "./fordon";
import { ALL_VEHICLE_TESTS as VEHICLE_TESTS } from "@/data/tester";

const URL = "https://www.akhusbilfritid.se/tester";

export const Route = createFileRoute("/tester/")({
  head: () => ({
    meta: [
      { title: "Långtidstester av husbilar – egna mätvärden | AK Husbil" },
      {
        name: "description",
        content:
          "Våra egna långtidstester: uppmätt bränsleförbrukning, gasolekonomi vid −20 °C, fuktvärden efter tre vintrar och riktiga ägarberättelser.",
      },
      {
        name: "keywords",
        content:
          "husbil test, husbil långtidstest, rapido test, husbil erfarenheter, husbil bränsleförbrukning test, husbil vintertest",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Långtidstester av husbilar – egna mätvärden" },
      {
        property: "og:description",
        content: "Mätvärden från vår egen verkstad i Kalix: fukt, gasol, förbrukning och ägarberättelser.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${URL}#collection`,
          name: "Långtidstester av husbilar",
          url: URL,
          inLanguage: "sv-SE",
          about: "Oberoende långtidstester av husbilar med egna mätvärden",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Tester", item: URL },
          ],
        }),
      },
    ],
  }),
  component: TesterIndex,
});

function TesterIndex() {
  return (
    <>
      <PageHeader
        label="Tester · Egna mätvärden"
        title={
          <>
            Långtidstester med <span className="font-italic-display text-accent">riktiga</span> siffror.
          </>
        }
        sub="Vi provkör inte över en helg. Vi mäter fukt i tolv punkter, väger gasolflaskor före och efter köldknäppar och följer samma bilar genom vår verkstad år efter år."
      />

      <section className="container-edge pb-8 pt-16 md:pt-20">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { icon: Thermometer, h: "Mätt i −22 °C", p: "Gasolförbrukning vägd, golv- och dubbelgolvstemperatur loggad." },
            { icon: Gauge, h: "Loggad förbrukning", p: "Tankningsdata från ägarna, inte uppskattningar från broschyren." },
            { icon: Ruler, h: "Fukt i 12 punkter", p: "Samma bilar mätta år efter år — vi ser hur de faktiskt åldras." },
          ].map((c) => (
            <div key={c.h} className="rounded-3xl border border-border bg-card p-6">
              <c.icon size={18} className="text-accent" />
              <h2 className="mt-3 font-display text-2xl leading-tight">{c.h}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.p}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-edge py-12 md:py-16">
        <ul className="grid gap-6 md:grid-cols-2">
          {VEHICLE_TESTS.map((t) => (
            <li key={t.slug}>
              <Link
                to="/tester/$slug"
                params={{ slug: t.slug }}
                className="group flex h-full flex-col rounded-3xl border border-border bg-card p-8 hover-lift"
              >
                <p className="eyebrow">{t.category}</p>
                <h2 className="mt-4 font-display text-3xl leading-tight">{t.h1}</h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{t.summary}</p>
                <dl className="mt-6 grid grid-cols-3 gap-3 text-xs text-muted-foreground">
                  <div>
                    <dt className="uppercase tracking-wider">Period</dt>
                    <dd className="mt-1 text-foreground">{t.testPeriod}</dd>
                  </div>
                  <div>
                    <dt className="uppercase tracking-wider">Sträcka</dt>
                    <dd className="mt-1 text-foreground">{t.testKm}</dd>
                  </div>
                  <div>
                    <dt className="uppercase tracking-wider">Underlag</dt>
                    <dd className="mt-1 text-foreground">{t.units}</dd>
                  </div>
                </dl>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-accent">
                  Läs testet{" "}
                  <ArrowUpRight
                    size={14}
                    className="transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-12 rounded-3xl border border-border bg-muted/40 p-8">
          <h2 className="font-display text-3xl">Så gör vi våra tester</h2>
          <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground/85">
            <p>
              Underlaget kommer från fordon vi själva sålt, servat och fuktmätt i Kalix. Varje exemplar mäts i minst tolv
              punkter med insticksprob: golvkant, bakvägg, hjulhus, takluckor, fönsterhörn och genomföringar. Gränsvärdet vi
              arbetar mot är 15 % — över det åtgärdar vi.
            </p>
            <p>
              Gasolförbrukning vägs med kranvåg före och efter kalla perioder. Bränsleförbrukning kommer från ägarnas
              tankningsloggar över hela ägandetiden, inte från en enskild resa. Där ett värde bygger på färre än tre exemplar
              skriver vi ut det i texten.
            </p>
            <p>
              Vi mäter medvetet i norrländskt klimat. En husbil som fungerar i februari i Kalix fungerar överallt i Sverige —
              det omvända gäller inte.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <Link to="/erfarenheter" className="rounded-full border border-border px-4 py-2 hover:border-accent">
              Erfarenhetsrapporter från ägare
            </Link>
            <Link to="/guider" className="rounded-full border border-border px-4 py-2 hover:border-accent">
              Guider
            </Link>
            <Link to="/fordon" className="rounded-full bg-primary px-4 py-2 text-primary-foreground">
              Se vårt lager
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
